// HRBonusSrazkaVyplatitModal — modal pro oznaceni polozek jako Vyplaceno.
//
// Pouziti:
//   - single: ids = [42]
//   - bulk:   ids = [42, 43, 44]
// Vybereš mesic_vyplaceni, klikne se Vyplatit, posle se na hr-bs-bulk
// (i pro single, bulk endpoint to zvladne s 1 id).

import React, { useMemo, useState } from "react";
import useEscClose from "../lib/useEscClose.js";
import { CheckCheck, Loader2, AlertCircle } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { Modal, HlavickaOkna } from "./ui.jsx";

const MONTHS_CS = ["Leden","Únor","Březen","Duben","Květen","Červen","Červenec","Srpen","Září","Říjen","Listopad","Prosinec"];
function mesicLabel(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_CS[parseInt(mm, 10) - 1]} ${y}`;
}

export default function HRBonusSrazkaVyplatitModal({ ids, defaultMesic, onClose, onSaved }) {
  useEscClose(onClose); // Esc zavre dialog
  const today = new Date().toISOString().substring(0, 7);
  const [mesic, setMesic] = useState(defaultMesic || today);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const monthOptions = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = -3; i < 15; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      arr.push({ value: m, label: mesicLabel(m) });
    }
    return arr.sort((a, b) => (a.value < b.value ? 1 : -1));
  }, []);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await apiFetch("/api/hr-bs-bulk", {
        method: "POST",
        body: JSON.stringify({ ids, action: "vyplatit", mesic_vyplaceni: mesic }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved && onSaved(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={CheckCheck} nazev="Označit jako vyplaceno" onClose={onClose} />}
      footer={
        <>
          <button onClick={onClose} disabled={saving} className="btn btn-secondary">Zrušit</button>
          <button onClick={handleSubmit} disabled={saving} className="btn btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
            Vyplatit ({ids.length})
          </button>
        </>
      }
    >
      <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900">
            Označuješ <b className="tabular-nums">{ids.length}</b> položek jako vyplacené.
            Položky, které nejsou ve stavu Čeká na vyplacení nebo Čeká na splnění, budou přeskočeny.
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Měsíc proplacení</label>
            <select
              value={mesic}
              onChange={(e) => setMesic(e.target.value)}
              className="field w-full"
            >
              {monthOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">
              Měsíc, kdy byly nebo budou tyto položky proplaceny ve výplatě nebo faktuře.
            </div>
          </div>
        </div>

    </Modal>
  );
}
